import React, { useRef, useState } from 'react';
import { graphql } from 'gatsby';
import { get } from 'lodash';
import isEqual from 'react-fast-compare';
import useWindowSize from '@rooks/use-window-size';

import Layout from '../components/layout';
import CoverSlide from '../components/cover-slide';
import SimpleImageSlide from '../components/simple-image-slide';
import SnappingSlider from '../components/snapping-slider';
import TextSlide from '../components/text-slide';
import QuoteSlide from '../components/quote-slide';
import StatSlide from '../components/stat-slide';
import StorySlide from '../components/story-slide';
import VideoSlide from '../components/video-slide';
import ShareSlide from '../components/share-slide';
import ProgressSlide from '../components/progress-slide';
import MenuToolbar from '../components/menu-toolbar';
import MobileMenu from '../components/mobile-menu';
import { forTabletLandscape } from '../utils/responsive';
import { twitterShare, facebookShare, linkedInShare } from '../utils/social';

const StoryPage = ({ data, location }) => {
    const sliderRef = useRef(null);
    const { innerWidth } = useWindowSize();

    const { page, socialSettings } = data.wagtail;
    if (!page) return null;

    let social = {};
    if (typeof window != `undefined`) {
        social = {
            twitter: twitterShare(
                window.location.href,
                socialSettings.defaultSharingText,
                socialSettings.twitterHandle,
            ),
            linkedin: linkedInShare(window.location.href, get(socialSettings, 'defaultSharingText')),
        };

        if (page.facebookAppId) {
            social['facebook'] = facebookShare(
                page.facebookAppId,
                window.location.href,
                get(socialSettings, 'defaultSharingText'),
            );
        }
    }

    const slides = page.body.map((block) => {
        switch (block.blockType) {
            case 'ImageBlock':
                return (props) => (
                    <SimpleImageSlide
                        slideType="ImageBlock"
                        caption={block.caption}
                        backgroundImage={block.image}
                        {...props}
                    />
                );

            case 'RichTextBlock':
                return (props) => <TextSlide slideType="RichTextBlock" text={block.value} {...props} />;

            case 'QuoteBlock':
                return (props) => (
                    <QuoteSlide
                        slideType="QuoteBlock"
                        quote={block.quote}
                        cite={block.attribution}
                        backgroundImage={block.image}
                        {...props}
                    />
                );

            case 'ResultsTextAndNumberBlock': {
                const chartData = block.textAndNumberResults.map((chart) => ({
                    type: 'number',
                    value: chart.number,
                    label: chart.text,
                    icon: chart.icon,
                }));
                return (props) => <StatSlide slideType="StatSlide" title={block.title} charts={chartData} {...props} />;
            }

            case 'ResultsChartBlock': {
                const chartData = block.chartResults.map((chart) => ({
                    type: 'progress',
                    value: chart.percentage,
                    label: chart.text,
                    unit: '%',
                }));
                return (props) => <StatSlide slideType="StatSlide" title={block.title} charts={chartData} {...props} />;
            }

            case 'ResultsComparisonChartBlock': {
                const chartData = block.comparisonChartResults.map((chart) => {
                    const total = chart.number1 + chart.number2;
                    const percentage1 = (100 / total) * chart.number1;
                    const percentage2 = (100 / total) * chart.number2;

                    return {
                        type: 'donut',
                        data: [
                            {
                                value: chart.showAsPercentage ? percentage1 : chart.number1,
                                label: chart.text1,
                                unit: chart.showAsPercentage ? '%' : ' ',
                            },
                            {
                                value: chart.showAsPercentage ? percentage2 : chart.number2,
                                label: chart.text2,
                                unit: chart.showAsPercentage ? '%' : ' ',
                            },
                        ],
                    };
                });
                return (props) => <StatSlide slideType="StatSlide" title={block.title} charts={chartData} {...props} />;
            }

            case 'StoryScrollerBlock':
                const sections = block.slides.map((slide) => {
                    if (slide.blockType == 'StoryScrollerIntro') {
                        return {
                            type: 'text',
                            title: slide.title,
                            content: slide.text,
                            image: slide.background,
                            galleryImage: slide.background?.galleryImage?.src,
                        };
                    }

                    if (slide.blockType == 'StoryScrollerQuote') {
                        return {
                            type: 'blockquote',
                            quote: slide.quote,
                            cite: slide.attribution,
                            image: slide.background,
                            galleryImage: slide.background?.galleryImage?.src,
                        };
                    }

                    if (slide.blockType == 'StoryScrollerStatistic') {
                        return {
                            type: 'stat-label',
                            stat: {
                                value: slide.statistic,
                            },
                            label: slide.text,
                            image: slide.background,
                            galleryImage: slide.background?.galleryImage?.src,
                        };
                    }

                    return {};
                });

                return {
                    indexControl: (indexCount) => {
                        return forTabletLandscape(innerWidth) ? indexCount + sections.length - 1 : indexCount;
                    },
                    Component: (props) => (
                        <StorySlide
                            slideType="StorySlide"
                            sections={sections}
                            extraImages={block.extraImages || []}
                            {...props}
                        />
                    ),
                };

            case 'VideoBlock':
                return (props) => (
                    <VideoSlide
                        slideType="VideoSlide"
                        promptText={block.title}
                        url={block.videoUpload?.url || block.youtubeLink?.url}
                        isYoutubeVideo={!!block.youtubeLink}
                        {...props}
                    />
                );

            case 'TimelineBlock':
                const slides = block.blocks.map((slide) => ({
                    label: slide.title,
                    image: get(slide, 'image.cover.src'),
                }));
                return (props) => <ProgressSlide slideType="TimelineBlock" slides={slides} {...props} />;

            default:
                return null;
        }
    });

    return (
        <Layout
            title={page.seoTitle}
            slug={page.slug}
            coverImage={page.coverImage.cover}
            description={page.seoDescription}
            icon={get(page, 'coverLogo.appleIcon.src')}
        >
            <SnappingSlider
                ref={sliderRef}
                slides={[
                    (props) => (
                        <CoverSlide
                            title={page.title}
                            orgName={page.organisation.title}
                            subtitle={page.subtitle}
                            backgroundImage={page.coverImage}
                            logo={page.coverLogo.src}
                            slideType="CoverSlide"
                            {...props}
                        />
                    ),
                    ...slides.filter((slide) => !!slide),
                    (props) => (
                        <ShareSlide
                            socialLinks={social}
                            text={page.thankYouText}
                            backgroundImage={page.thankYouImage}
                            slideType="ShareSlide"
                            {...props}
                        />
                    ),
                ]}
                toolbars={(slides, currentSlide) => {
                    return (
                        <>
                            <MenuToolbar
                                currentSlide={currentSlide}
                                totalSlides={slides.length}
                                navigate={(index) => sliderRef.current.navigateToIndex(index)}
                                navigateToHome={() => sliderRef.current.navigateToIndex(0)}
                                navigateToShare={() => sliderRef.current.navigateToIndex(slides.length - 1)}
                            />
                            <MobileMenu
                                currentSlide={currentSlide}
                                totalSlides={slides.length}
                                nextSlide={() => sliderRef.current.nextSlide()}
                                prevSlide={() => sliderRef.current.prevSlide()}
                                navigateToHome={() => sliderRef.current.navigateToIndex(0)}
                                shareOptions={social}
                            />
                        </>
                    );
                }}
            />
        </Layout>
    );
};

export const query = graphql`
    query StoryQuery($slug: String) {
        wagtail {
            page(slug: $slug) {
                ... on Story {
                    title
                    subtitle
                    slug
                    seoTitle
                    seoDescription
                    facebookAppId
                    organisation {
                        title
                    }
                    coverImage {
                        ...TracedWagtailImage
                    }
                    coverLogo: getCoverLogo {
                        src
                        appleIcon: rendition(fill: "180x180") {
                            src
                        }
                    }
                    thankYouText
                    thankYouImage {
                        ...TracedWagtailImage
                    }
                    body {
                        id
                        blockType

                        ... on TimelineBlock {
                            field
                            blocks {
                                ... on TimelineEntry {
                                    title
                                    image {
                                        ...TracedWagtailImage
                                    }
                                }
                            }
                        }

                        ... on QuoteBlock {
                            quote
                            attribution
                            image {
                                ...TracedWagtailImage
                            }
                        }

                        ... on ResultsTextAndNumberBlock {
                            title
                            textAndNumberResults {
                                icon
                                number
                                text
                            }
                        }

                        ... on ResultsChartBlock {
                            title
                            chartResults {
                                percentage
                                text
                            }
                        }

                        ... on ResultsComparisonChartBlock {
                            title
                            comparisonChartResults {
                                text1
                                number1
                                text2
                                number2
                                showAsPercentage
                            }
                        }

                        ... on StoryScrollerBlock {
                            slides {
                                ... on StoryScrollerIntro {
                                    blockType
                                    field
                                    title
                                    text
                                    background {
                                        ...LightboxImage
                                    }
                                }
                                ... on StoryScrollerQuote {
                                    blockType
                                    quote
                                    blockType
                                    attribution
                                    background {
                                        ...LightboxImage
                                    }
                                }
                                ... on StoryScrollerStatistic {
                                    blockType
                                    field
                                    statistic
                                    text
                                    background {
                                        ...LightboxImage
                                    }
                                }
                            }
                            extraImages {
                                caption
                                image {
                                    ...LightboxImage
                                }
                            }
                        }

                        ... on ImageBlock {
                            caption
                            image {
                                ...TracedWagtailImage
                            }
                        }

                        ... on VideoBlock {
                            title
                            youtubeLink {
                                url
                            }
                            videoUpload {
                                url
                            }
                        }

                        ... on RichTextBlock {
                            value
                        }
                    }
                }
            }
            socialSettings: setting(name: "SocialMediaSettings") {
                ... on SocialMediaSettings {
                    siteName
                    defaultSharingText
                    twitterHandle
                    facebookAppId
                }
            }
        }
    }
`;

export default StoryPage;
